import React from 'react'
import { Control, FieldErrors } from 'react-hook-form'
import s from './styles.module.css'
import {
  TextQuestion,
  LongTextQuestion,
  MultiSelectQuestion,
  SingleSelectQuestion,
  BooleanQuestion,
  DropdownQuestion,
  DateQuestion,
  HeightQuestion,
  WeightQuestion,
  InsuranceMembershipIdQuestion,
} from './index'
import { FreeFormSkipQuestion } from './free-form-skip'
import { CustomQuestion } from './custom'

interface SurveyQuestion {
  id: number
  text: string
  type: string
  isRequired?: boolean
  maxSelections?: number
  component?: string
  responses?: Array<{
    id: number
    text: string
    nestedQuestions?: SurveyQuestion[]
  }>
  richTextNote?: {
    content: any[]
  }
}

interface QuestionFactoryProps {
  question: SurveyQuestion
  // React Hook Form mode (for survey-renderer)
  control?: Control<any>
  errors?: FieldErrors<any>
  // Zustand mode (for question-page)
  value?: any
  skipValue?: boolean
  error?: string
  onChange?: (value: any) => void
  onBlur?: () => void
  onSkipChange?: (skipValue: boolean) => void
  // Shared props
  clientId?: number
  responseByQuestionId?: Record<number, any[]>
  allQuestions?: Array<{ id: number | string }>
}

const QuestionFactory: React.FC<QuestionFactoryProps> = ({
  question,
  control,
  errors,
  value,
  skipValue,
  error,
  onChange,
  onBlur,
  onSkipChange,
  clientId,
  responseByQuestionId,
  allQuestions,
}) => {
  // Common props for all question types
  // Pass all props (both RHF and Zustand) - components will detect mode internally
  const commonProps = {
    question,
    clientId,
    responseByQuestionId,
    allQuestions,
    // RHF mode props
    control,
    errors,
    // Zustand mode props
    value,
    skipValue,
    error,
    onChange,
    onBlur,
    onSkipChange,
  }

  switch (question.type) {
    case 'text':
      return <TextQuestion {...commonProps} />

    case 'longText':
      return <LongTextQuestion {...commonProps} />

    case 'multiSelect':
      return <MultiSelectQuestion {...commonProps} />

    case 'select':
      return <SingleSelectQuestion {...commonProps} />

    case 'dropdown':
      return <DropdownQuestion {...commonProps} />

    case 'boolean':
      return <BooleanQuestion {...commonProps} />

    case 'date':
      return <DateQuestion {...commonProps} />

    case 'heightPicker':
      return <HeightQuestion {...commonProps} />

    case 'weightPicker':
      return <WeightQuestion {...commonProps} />

    case 'insuranceMembershipId':
      return <InsuranceMembershipIdQuestion {...commonProps} />

    case 'custom':
      return (
        <CustomQuestion
          {...commonProps}
          question={question}
          control={control}
          errors={errors}
          value={value}
          skipValue={skipValue}
          error={error}
          onChange={onChange}
          onSkipChange={onSkipChange}
        />
      )

    case 'freeFormSkip':
      return <FreeFormSkipQuestion {...commonProps} />

    default:
      return (
        <div className={s.question}>
          <label className={s.questionText}>
            {question.text}
            {question.isRequired && <span className={s.required}> *</span>}
          </label>
          <div className={s.questionType}>
            Unsupported question type: {question.type}
          </div>
        </div>
      )
  }
}

export default QuestionFactory
